import { formatISO, isDate, isFuture, parseISO } from 'date-fns'
import { boolean, number, object, string } from 'yup'

const addPrefix = (obj: { [key: string]: any }, prefix: string) => {
  const newObj: { [key: string]: any } = {}
  Object.keys(obj).forEach((key) => {
    newObj[`${prefix}${key}`] = obj[key]
  })
  return newObj
}

const addressValidation = (prefix: string, formStep: number, total: number) => {
  const address = {
    address_line1: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .required('Address line 1 is required')
        .matches(
          /^[a-zA-Z0-9 @&.,~_-]{1,60}$/,
          'Address line 1 can only contain letters and basic punctuation',
        )
        .max(60, 'Address line 1 must be at most 60 characters')
        .trim(),
      otherwise: string().trim(),
    }),
    address_line2: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .matches(
          /^[a-zA-Z0-9 @&.,~_-]{0,40}$/,
          'Address line 2 can only contain letters and basic punctuation',
        )
        .max(40, 'Address line 2 must be at most 40 characters')
        .trim(),
      otherwise: string().trim(),
    }),
    town: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .required('Town is required')
        .matches(
          /^[a-zA-Z0-9 @&.,~_-]{1,40}$/,
          'Town can only contain letters and basic punctuation',
        )
        .max(40, 'Town must be at most 40 characters')
        .trim(),
      otherwise: string().trim(),
    }),
    county: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .matches(
          /^[a-zA-Z0-9 @&.,~_-]{0,40}$/,
          'County can only contain letters and basic punctuation',
        )
        .max(40, 'County must be at most 40 characters')
        .trim(),
      otherwise: string().trim(),
    }),
    postcode: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .required('Postcode is required')
        .matches(
          /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[\s]?[0-9][A-Za-z]{2}|(E|N|W|S|NW|SE|SW|EC|WC)[0-9Rr][0-9A-Za-z]?[\s]?[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2})$/,
          'Please enter a valid postcode',
        )
        .max(8, 'Postcode must be at most 8 characters')
        .trim(),
      otherwise: string().trim(),
    }),
    years: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .required('Years is required')
        .matches(/^[0-9]{1,2}$/, 'Years must be a number')
        .test('years', 'Years must be at least 0', (value) => {
          return parseInt(value, 10) >= 0
        })
        .test('years', 'Years must be at most 99', (value) => {
          return parseInt(value, 10) <= 99
        })
        .max(2, 'Years must be at most 2 characters'),
      otherwise: string().trim(),
    }),
    months: string().when(['step', 'total_addresses'], {
      is: (step: number, total_addresses: number) =>
        step === formStep && total_addresses >= total,
      then: string()
        .required('Months is required')
        .matches(/^[0-9]{1,2}$/, 'Months must be a number')
        .test('months', 'Months must be at least 0', (value) => {
          return parseInt(value, 10) >= 0
        })
        .test('months', 'Months must be at most 11', (value) => {
          return parseInt(value, 10) <= 11
        })
        .max(2, 'Months must be at most 2 characters'),
      otherwise: string().trim(),
    }),
  }
  return addPrefix(address, prefix)
}

const employerValidation = (
  prefix: string,
  formStep: number,
  total: number,
) => {
  const employer = {
    employment_type: string().when(['step', 'total_employers'], {
      is: (step: number, total_employers: number) =>
        step === formStep && total_employers >= total,
      then: string().required('Employment type is required').trim(),
      otherwise: string().trim(),
    }),
    employment_status: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string().required('Employment status is required').trim(),
        otherwise: string().trim(),
      },
    ),
    occupation: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string().required('Occupation is required').trim(),
        otherwise: string().trim(),
      },
    ),
    job_title: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Job title is required')
          .matches(
            /^[a-zA-Z0-9 @&.,~_-]{1,40}$/,
            'Job title can only contain letters and basic punctuation',
          )
          .max(40, 'Job title must be at most 40 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Employer is required')
          .matches(
            /^[a-zA-Z0-9 @&.,~_-]{1,40}$/,
            'Employer can only contain letters and basic punctuation',
          )
          .max(40, 'Employer must be at most 40 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer_address_line1: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Address line 1 is required')
          .matches(
            /^[a-zA-Z0-9 @&.,~_-]{1,60}$/,
            'Address line 1 can only contain letters and basic punctuation',
          )
          .max(60, 'Address line 1 must be at most 60 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer_address_line2: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .matches(
            /^[a-zA-Z0-9 @&.,~_-]{0,40}$/,
            'Address line 2 can only contain letters and basic punctuation',
          )
          .max(40, 'Address line 2 must be at most 40 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer_town: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Town is required')
          .matches(
            /^[a-zA-Z0-9 @&.,~_-]{1,40}$/,
            'Town can only contain letters and basic punctuation',
          )
          .max(40, 'Town must be at most 40 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer_county: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .matches(
            /^[a-zA-Z0-9 @&.,~_-]{0,40}$/,
            'County can only contain letters and basic punctuation',
          )
          .max(40, 'County must be at most 40 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer_postcode: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Postcode is required')
          .matches(
            /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[\s]?[0-9][A-Za-z]{2}|(E|N|W|S|NW|SE|SW|EC|WC)[0-9Rr][0-9A-Za-z]?[\s]?[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2})$/,
            'Please enter a valid postcode',
          )
          .max(8, 'Postcode must be at most 8 characters')
          .trim(),
        otherwise: string().trim(),
      },
    ),
    employer_years: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Years is required')
          .matches(/^[0-9]{1,2}$/, 'Years must be a number')
          .test('years', 'Years must be at least 0', (value) => {
            return parseInt(value, 10) >= 0
          })
          .test('years', 'Years must be at most 99', (value) => {
            return parseInt(value, 10) <= 99
          })
          .max(2, 'Years must be at most 2 characters'),
        otherwise: string().trim(),
      },
    ),
    employer_months: string().when(
      ['step', 'total_employers', `${prefix}employment_type`],
      {
        is: (step: number, total_employers: number, employment_type: string) =>
          step === formStep &&
          total_employers >= total &&
          ['1', '3', '5'].some((value) => employment_type.includes(value)),
        then: string()
          .required('Months is required')
          .matches(/^[0-9]{1,2}$/, 'Months must be a number')
          .test('months', 'Months must be at least 0', (value) => {
            return parseInt(value, 10) >= 0
          })
          .test('months', 'Months must be at most 11', (value) => {
            return parseInt(value, 10) <= 11
          })
          .max(2, 'Months must be at most 2 characters'),
        otherwise: string().trim(),
      },
    ),
  }
  return addPrefix(employer, prefix)
}

const PreApprovalSchema = object().shape({
  step: number(),
  dealer: number(),
  dealer_name: string().trim(),
  title: string().when('step', {
    is: 2,
    then: string().required('Title is required').trim(),
    otherwise: string().trim(),
  }),
  first_name: string().when('step', {
    is: 2,
    then: string()
      .required('First name is required')
      .matches(
        /^[a-zA-Z0-9 @&.,~_-]{1,40}$/,
        'First name can only contain letters and basic punctuation',
      )
      .min(2, 'First name must be at least 2 characters')
      .max(40, 'First name must be at most 40 characters')
      .trim(),
    otherwise: string().trim(),
  }),
  last_name: string().when('step', {
    is: 2,
    then: string()
      .required('Last name is required')
      .matches(
        /^[a-zA-Z0-9 @&.,~_-]{1,30}$/,
        'Last name can only contain letters and basic punctuation',
      )
      .min(2, 'Last name must be at least 2 characters')
      .max(30, 'Last name must be at most 30 characters')
      .trim(),
    otherwise: string().trim(),
  }),
  email: string().when('step', {
    is: 2,
    then: string()
      .required('Email is required')
      .matches(
        /^([a-z0-9]+(?:[._-][a-z0-9]+)*)@([a-z0-9]+(?:[.-][a-z0-9]+)*\.[a-z]{2,})$/,
        'Email must be a valid email address',
      )
      .max(60, 'Email must be at most 60 characters')
      .transform((value) => value.replace('&', ''))
      .trim(),
    otherwise: string().trim(),
  }),
  tel: string().when('step', {
    is: 2,
    then: string()
      .required('Phone is required')
      .matches(/^\d{7,11}$/, {
        message: 'Please enter a valid mobile phone number',
        excludeEmptyString: true,
      })
      .trim(),
    otherwise: string().trim(),
  }),
  home_tel: string().when('step', {
    is: 2,
    then: string()
      .matches(/^\d{7,11}$/, {
        message: 'Please enter a valid home phone number',
        excludeEmptyString: true,
      })
      .trim(),
    otherwise: string().trim(),
  }),
  dob: string().when('step', {
    is: 2,
    then: string()
      .required('Please enter your date of birth')
      .test(
        'is-future',
        'Date of birth cannot be in the future',
        (value) => !isFuture(parseISO(value)),
      )
      .test('is-date', 'Please enter a valid date of birth', (value) =>
        isDate(parseISO(value)),
      )
      .test('is-adult', 'You must be at least 18 years old', (value) => {
        const dob = parseISO(value)
        const now = new Date()
        const age = now.getFullYear() - dob.getFullYear()
        return age >= 18
      })
      .test('is-not-too-old', 'You must be at most 99 years old', (value) => {
        const dob = parseISO(value)
        const now = new Date()
        const age = now.getFullYear() - dob.getFullYear()
        return age <= 99
      })
      .transform((value) => {
        if (value) {
          const parts = value.split('/')
          const formattedDate = new Date(parts[2], parts[1] - 1, parts[0])
          return formatISO(formattedDate, { representation: 'date' })
        }
        return value
      }),
    otherwise: string().trim(),
  }),
  gender: string().when('step', {
    is: 2,
    then: string().required('Please select a gender').trim(),
    otherwise: string().trim(),
  }),
  marital: string().when('step', {
    is: 2,
    then: string().required('Please select a marital status').trim(),
    otherwise: string().trim(),
  }),
  residence: string().when('step', {
    is: 2,
    then: string().required('Please select a residential status').trim(),
    otherwise: string().trim(),
  }),
  ...addressValidation('', 3, 1),
  ...addressValidation('second_', 3, 2),
  ...addressValidation('third_', 3, 3),
  ...employerValidation('', 4, 1),
  ...employerValidation('second_', 4, 2),
  privacy_notice: string().when('step', {
    is: 5,
    then: string().required('Please accept the privacy notice').trim(),
    otherwise: string().trim(),
  }),
  my_toyota: string().trim(),
  communication_reminders: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  communication_relevantoffers: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  communication_surveys: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  communication_events: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  contact_phone: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  contact_email: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  contact_sms: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  contact_post: string().when('step', {
    is: 5,
    then: string().required('Please accept communication reminders').trim(),
    otherwise: string().trim(),
  }),
  consent: object({
    user: boolean(),
  }),
})

export default PreApprovalSchema
